import { ChangeEventHandler, MutableRefObject, useCallback, useEffect, useState } from "react";
import { AnnotationFormFields, AnnotationUpdatedFields } from "../types/annotation.types";
import { AnnotationModel, AnnotationType } from "../models/annotation.model";
import { useAnnotationFormCreationToaster } from "./use-annotation-form-creation-toaster.hook";
import { compressorUtil } from "../utils/compressor.util";

export const useAnnotationForm = (
  annotationToVisualize: AnnotationModel | null,
  createAnnotation: (annotation: {
    type: AnnotationType;
    title: string;
    content?: string;
    pictureBinary?: string;
    status?: string;
    equipmentCode?: string;
  }) => void,
  updateAnnotation: (id: string, updatedAnnotationFields: AnnotationUpdatedFields) => void,
  deleteAnnotation: (id: string, fileName: string) => void,
  titleRef: MutableRefObject<HTMLIonInputElement | null>
) => {
  const resetForm = useCallback(() => {
    setAnnotationFormFields({
      type: undefined,
      title: "",
      content: "",
      pictureBinary: null,
      status: null,
      equipmentCode: null
    });
  }, []);

  const [annotationFormFields, setAnnotationFormFields] = useState<
    Partial<AnnotationFormFields>
  >(
    annotationToVisualize === null
      ? {
          type: undefined,
          equipmentCode: null,
        }
      : {
          type: annotationToVisualize.type,
          title: annotationToVisualize.title,
          content: annotationToVisualize.content,
          pictureBinary: annotationToVisualize.pictureBinary,
          status: annotationToVisualize.status,
          equipmentCode: annotationToVisualize.equipmentCode,
        }
  );
  const onPictureInputValueChanged = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      if (!event.target.files || event.target.files.length !== 1) {
        return;
      }
      const file = event.target.files[0];

      return compressorUtil(
        file,
        (binary) =>
          setAnnotationFormFields((oldAnnotationFormFields) => ({
            ...oldAnnotationFormFields,
            pictureBinary: binary,
          })),
        () =>
          setAnnotationFormFields((oldAnnotationFormFields) => ({
            ...oldAnnotationFormFields,
            pictureBinary: null,
          }))
      );
    },
    [setAnnotationFormFields]
  );

  const { showCreationSuccess, showTooLargeFail, showAnyErrorFail } = useAnnotationFormCreationToaster();
  const onCreateButtonClicked = useCallback(async () => {
    if (annotationFormFields.type && annotationFormFields.title) {
      try {
        await createAnnotation({
          type: annotationFormFields.type,
          title: annotationFormFields.title,
          content: annotationFormFields.content,
          pictureBinary: annotationFormFields.pictureBinary ?? undefined,
          status: annotationFormFields.status ?? 'NEW',
          equipmentCode: annotationFormFields.equipmentCode ?? undefined
        });
        await showCreationSuccess();
        resetForm();
      } catch (error: any) {
        if (error.status === 413) {
          setAnnotationFormFields((oldAnnotationFormFields) => ({
            ...oldAnnotationFormFields,
            pictureBinary: null,
          }));
          await showTooLargeFail();
        } else {
          await showAnyErrorFail();
        }
      }
    }
  }, [createAnnotation, annotationFormFields, showCreationSuccess, showTooLargeFail, showAnyErrorFail, resetForm]);

  const onUpdateButtonClicked = useCallback(() => {
    if (annotationFormFields.type && annotationFormFields.title) {
      const originalAnnotation = annotationToVisualize!;
      const updatedAnnotationFields: AnnotationUpdatedFields = {};
      if (originalAnnotation.title !== annotationFormFields.title) {
        updatedAnnotationFields.title = annotationFormFields.title;
      }
      if (originalAnnotation.type !== annotationFormFields.type) {
        updatedAnnotationFields.type = annotationFormFields.type;
      }
      if (originalAnnotation.content !== annotationFormFields.content) {
        updatedAnnotationFields.content = annotationFormFields.content;
      }
      if (
        annotationFormFields.pictureBinary !== undefined &&
        originalAnnotation.pictureBinary !== annotationFormFields.pictureBinary
      ) {
        updatedAnnotationFields.pictureBinary = annotationFormFields.pictureBinary ?? "";
      }
      if (originalAnnotation.equipmentCode !== annotationFormFields.equipmentCode) {
        updatedAnnotationFields.equipmentCode = annotationFormFields.equipmentCode ?? "";
      }

      updatedAnnotationFields.status = annotationFormFields.status;
      
      updateAnnotation(originalAnnotation.id, updatedAnnotationFields);
      resetForm();
    }
  }, [updateAnnotation, annotationToVisualize, annotationFormFields, resetForm]);

  const onDeleteButtonClicked = useCallback(() => {
    deleteAnnotation(annotationToVisualize!.id, annotationToVisualize?.fileName ?? "");
  }, [deleteAnnotation, annotationToVisualize]);

  useEffect(() => {
    requestAnimationFrame(() => titleRef.current?.setFocus());
  }, [titleRef]);

  useEffect(() => {
    if (annotationToVisualize === null) {
      resetForm();
    }
  }, [annotationToVisualize, resetForm]);

  return {
    annotationFormFields,
    setAnnotationFormFields,
    onPictureInputValueChanged,
    onCreateButtonClicked,
    onUpdateButtonClicked,
    onDeleteButtonClicked,
  };
};
